/**
 * CatalogPicker — a read-only picker bound to an Oxbo-M resolved catalog
 * (accounts / tubulars / formations / activity codes). The design screens use
 * it to pick a value from the masters without letting the planner invent one
 * (masters are Oxbo-M's to write). Loads via mApi; degrades to a plain text
 * value while loading / when the catalog is empty, so it never blocks input.
 *
 * The admin ↗ deep-link to the Oxbo-M catalog admin is gated (§22/§23): shown
 * only for data_admin, and the URL is omitted entirely for everyone else.
 */
import { useAppSelector } from "@app/hooks";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import type { PrefsState } from "@app/slices/prefsSlice";
import type { ResolvedCatalogEntry } from "@api/types";
import { asArray } from "@lib/asArray";

export interface CatalogPickerProps {
  /** Oxbo-M category key: "accounts" | "tubulars" | "formations" | … */
  categoryKey: string;
  value: string | null;
  onChange: (key: string | null, label: string | null) => void;
  asOf?: string;
  siteRegionKey?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Base URL of the Oxbo-M catalog admin, for the gated deep-link (admins only). */
  adminBaseUrl?: string;
}

export function CatalogPicker({
  categoryKey,
  value,
  onChange,
  asOf,
  siteRegionKey,
  placeholder = "—",
  disabled = false,
  adminBaseUrl,
}: CatalogPickerProps) {
  const role = useAppSelector((s: { prefs: PrefsState }) => s.prefs.role);
  const { data, isLoading } = useGetResolvedCatalogQuery({ categoryKey, asOf, siteRegionKey });
  // asArray (not `?? []`): a real backend can return a present-but-non-array
  // `entries` body ({} / null / error envelope); `?? []` only guards
  // null/undefined, so the .map below would throw and white-screen the app
  // (the Oxbo-Z prod crash class). See src/lib/asArray.ts.
  const entries = asArray<ResolvedCatalogEntry>(data?.entries);

  const isAdmin = role === "data_admin";

  return (
    <div className="catalog-picker" style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <select
        value={value ?? ""}
        disabled={disabled || isLoading}
        aria-label={categoryKey}
        onChange={(e) => {
          const key = e.target.value || null;
          const label = entries.find((x) => x.key === key)?.label ?? null;
          onChange(key, label);
        }}
      >
        <option value="">{placeholder}</option>
        {entries.map((en) => (
          <option key={en.key} value={en.key}>
            {en.label}
          </option>
        ))}
      </select>
      {isAdmin && adminBaseUrl && (
        <a
          className="catalog-admin-link"
          href={`${adminBaseUrl}/${encodeURIComponent(categoryKey)}`}
          target="_blank"
          rel="noreferrer"
          title="Open in Oxbo-M catalog admin"
          aria-label="Open in Oxbo-M catalog admin"
        >
          ↗
        </a>
      )}
    </div>
  );
}
